import type { CharacterSheetInterface } from "@/types/characters";
import type { AppliedConfiguration, ContainerEffect, SwadeModConfigurationInterface, SwadeModificationInterface } from "@/types/items";
import { containerDisplayName } from "@/appdata/container-fields";
import { recordsForSourceModification, resolvedModificationUuid } from "@/appdata/applied-configuration";

/**
 * A modification whose `element` arrives unset — `null`, `undefined` or `''`. On a container path
 * this means "prompt the player to pick exactly one target".
 */
export const isElementUnset = (element: unknown): boolean =>
    element === null
    || element === undefined
    || (typeof element === 'string' && element.length === 0);

/**
 * A modification whose `element` is an empty list. On a container path this means "prompt for one or
 * more": the add-time resolver turns it into a multi-select synthetic choice set, so a raw `[]`
 * reaching resolveContainerTargets means "not picked yet" and grants nothing.
 */
export const isEmptyList = (element: unknown): boolean =>
    Array.isArray(element) && element.length === 0;

/**
 * The union, preserving the meaning the two existing `character.*` dynamic resolvers rely on. Kept
 * so this split is additive: nothing that reads "empty" today changes behaviour.
 */
export const isElementEmpty = (element: unknown): boolean =>
    isElementUnset(element) || isEmptyList(element);

/**
 * A catalog or container uuid from one authored element entry, or null when the entry yields none.
 * Mirrors the core-skills applier's read (`element.map((item) => item.uuid)`), widened to accept the
 * bare strings that player records and hand-written data carry. Null means "drop it": a malformed
 * entry must neither match a container by accident nor collide rollback keys as "[object Object]".
 */
export const elementEntryUuid = (entry: unknown): string | null => {
    if (typeof entry === 'string') return entry;
    const uuid = (entry as { uuid?: unknown } | null)?.uuid;
    return typeof uuid === 'string' ? uuid : null;
};

/**
 * A container-path `element` that defers to a single-pick prompt: `null` or `''`, but NOT
 * `undefined`.
 *
 * NOT the same question as `isElementUnset`, and the difference is load-bearing. An ABSENT element
 * (`undefined`) means "modify the container that granted me" — mode `self`, resolved below without
 * asking anybody anything. Only an element the author explicitly emptied is a prompt.
 *
 * Exported because the prompt resolver in character-data.ts has to decide "is this a pick?" from
 * the modification alone, before it has a character to resolve against. Both it and the `prompt`
 * branch of resolveContainerTargets must answer that identically: match on `isElementUnset` instead
 * and every self-targeting container modification — the most common authored shape — opens a picker
 * it should never have opened.
 */
export const isPromptElement = (element: unknown): boolean =>
    element !== undefined && isElementUnset(element);

/** The five targeting modes an author can store (spec 2026-09-13 §2.2). */
export type AuthoredTargetMode = 'self' | 'pickOne' | 'pickMany' | 'specific' | 'allSpecified' | 'offered';

/**
 * An offered option (spec 2026-09-21 §5.1): a choice set the player may pick on a power card. Never
 * prompted, never applied by a direct applicator, never pencilled — the four gates read this one
 * predicate. The flag lives on the set; its rungs carry it nowhere.
 */
export const isOfferedOption = (configuration: SwadeModConfigurationInterface | null | undefined): boolean =>
    configuration?.offered === true;

/**
 * The ONE shape-to-mode rule. Every reader — the configurator's button state, the resolver's first
 * branch, the prompt/pencil/resolvable-modification checks — derives the authored mode from here,
 * so the rule cannot drift between them. `applyToAll` is consulted only beside a non-empty specific
 * list: anywhere else the element's own shape wins, so a malformed payload degrades to today's
 * behaviour rather than to a new one.
 */
export function authoredTargetMode(configuration: SwadeModConfigurationInterface | null | undefined): AuthoredTargetMode {
    // Ahead of every shape rule: an offered configuration is an option, whatever its element holds.
    if (isOfferedOption(configuration)) return 'offered';
    const element = configuration?.element;
    if (element === undefined) return 'self';
    if (isPromptElement(element)) return 'pickOne';
    if (isEmptyList(element)) return 'pickMany';
    return configuration?.applyToAll === true ? 'allSpecified' : 'specific';
}

/** The character arrays a container path can search. */
export type ContainerArrayName = 'equipment' | 'skills' | 'edges' | 'hindrances' | 'powers';

/** Supported container paths and the character array each one searches. */
export const CONTAINER_PATHS: Record<string, ContainerArrayName> = {
    'container.items': 'equipment',
    'container.skills': 'skills',
    'container.edges': 'edges',
    'container.hindrances': 'hindrances',
    // Both kinds of power live in `character.powers` and name their catalog element `selection`
    // (spec 2026-09-11 §2); a power is in use whenever it is present.
    'container.powers': 'powers',
};

/**
 * Own-property-safe lookup. `path` is author-supplied data and CONTAINER_PATHS is an object
 * literal, so a plain index with 'constructor' returns an inherited function, not undefined —
 * the resolver in character-data.ts documents exactly that hazard. Every read goes through here.
 */
export const supportedContainerPath = (
    path: string | null | undefined,
): ContainerArrayName | undefined =>
    typeof path === 'string' && Object.prototype.hasOwnProperty.call(CONTAINER_PATHS, path)
        ? CONTAINER_PATHS[path]
        : undefined;

/**
 * Anything but an ABSENT element can be redirected by the player. `undefined` is `self` — the
 * container that granted the modification — and is never offered.
 */
export const isRedirectableElement = (element: unknown): boolean => element !== undefined;

export type PickerMode = 'single' | 'multi';

/**
 * `[]` — the authored "pick one or more" — edits as a multi-select. Everything else redirectable —
 * `null`/`''` ("pick exactly one") and a specific list, a bare uuid or a legacy string list, whose
 * answer is one container (spec 2026-09-10 §2.1) — picks exactly one. `undefined` never reaches
 * here (see isRedirectableElement).
 */
export const pickerModeFor = (element: unknown): PickerMode =>
    isEmptyList(element) ? 'multi' : 'single';

/**
 * How a configuration resolved. `byContainer`: every match was a container's OWN uuid — the shape a
 * record holds. `bySelection`: at least one target matched by its catalog element — an authored
 * list, a legacy string list or a mixed one; since spec 2026-09-13 registration refuses this mode,
 * so a Specific Choices modification reaches the ledger only through a recorded pick. `all`: an
 * All Specified list, resolved by selection identity and re-resolved by every ledger reader.
 */
export type TargetMode = 'self' | 'byContainer' | 'bySelection' | 'all' | 'prompt' | 'unsupported';

export interface ContainerTargetResult {
    targets: unknown[];
    mode: TargetMode;
}

/**
 * Each container type names its catalog element differently. An ItemContainer and an EdgeContainer
 * hold `selection`, a SkillRank holds `skill`, a Drawback holds `hindrance`.
 *
 * Exported because it is one half of a write/read pair, not a convenience. The single-pick prompt
 * (character-data.ts) records THIS uuid as the chosen option's `element`, and the `bySelection`
 * branch below reads it back to decide which containers that pick matched. A second copy at the
 * write site that agreed today would resolve a player's pick to nothing the day either side changed.
 */
export const selectionUuid = (container: any): string | null =>
    container?.selection?.uuid ?? container?.skill?.uuid ?? container?.hindrance?.uuid ?? null;

/** The character's containers on a path, or [] when the path is unsupported or the array is missing. */
const containersOnPath = (character: CharacterSheetInterface, path: string | null | undefined): any[] => {
    const arrayName = supportedContainerPath(path);
    if (!arrayName) return [];
    const containers = (character as any)?.[arrayName];
    return Array.isArray(containers) ? containers : [];
};

/**
 * The containers a SPECIFIC-mode configuration may target: those on its path holding one of the
 * authored catalog elements. Selection identity only — container identity belongs to records, and
 * matching it here would let a player's pick masquerade as an authored target. Empty for every
 * other shape.
 */
export function eligibleContainerTargets(
    character: CharacterSheetInterface,
    configuration: SwadeModConfigurationInterface,
): any[] {
    const element = configuration?.element;
    if (element === undefined || isPromptElement(element) || isEmptyList(element)) return [];
    const wanted = (Array.isArray(element) ? element : [element])
        .map(elementEntryUuid)
        .filter((uuid): uuid is string => uuid !== null);
    return containersOnPath(character, configuration.path).filter((container) => {
        if (typeof container?.uuid !== 'string' || container.uuid.length === 0) return false;
        const uuid = selectionUuid(container);
        return uuid !== null && wanted.includes(uuid);
    });
}

/**
 * Who a container-target picker offers, by mode. Pick-one and pick-many carry no authored
 * restriction, so every container on the path that has a catalog element; specific mode offers
 * only the eligible ones. The add-time prompt, the pencil menu, the pencil's visibility and a power's
 * rung targets all read this, so they cannot disagree about who is offered. Given the `pick` that is
 * asking — its source and modification — a container another copy of that source already picked
 * with that modification is not offered (spec 2026-10-13 §4a, takenByAnotherCopy).
 */
export function candidateContainerTargets(
    character: CharacterSheetInterface,
    configuration: SwadeModConfigurationInterface,
    pick?: { sourceUuid: string, modUuid: string | undefined },
): any[] {
    const element = configuration?.element;
    if (element === undefined) return [];
    const candidates = isPromptElement(element) || isEmptyList(element)
        ? containersOnPath(character, configuration.path)
            .filter((container) => typeof container?.uuid === 'string' && selectionUuid(container) !== null)
        : eligibleContainerTargets(character, configuration);
    return pick ? candidates.filter((container) => !takenByAnotherCopy(character, container, pick)) : candidates;
}

/**
 * The authored configuration with its target filled in: the option a picker offers and the
 * configuration a record carries. Spread first, so path, detail, operator, value and valueType
 * survive and the pick applies through applyModification's container branch unchanged.
 */
export const targetedConfiguration = (
    configuration: SwadeModConfigurationInterface,
    container: any,
): SwadeModConfigurationInterface => ({
    ...configuration,
    element: container.uuid,
    label: containerDisplayName(container),
});

/**
 * The record the ChoiceApplicator would write for a pick — the same shape, so an auto-resolved
 * single candidate (spec §2.2) is indistinguishable from an answered prompt to every reader.
 */
export const containerTargetRecord = (
    sourceUuid: string,
    mod: SwadeModificationInterface,
    container: any,
): AppliedConfiguration => ({
    uuid: sourceUuid,
    sourceModUuid: mod.uuid,
    choiceSetUuid: mod.uuid,
    configuration: targetedConfiguration(mod.configuration, container),
});

/**
 * Which containers a `container.*` modification applies to.
 *
 * Does not mutate, does not prompt, and never rewrites the authored `configuration.element` —
 * `matchesConfiguration` compares that field with `===` to re-highlight a previously chosen option,
 * so rewriting it would break choice reuse. Resolved targets are the caller's to record separately.
 */
export function resolveContainerTargets(
    configuration: SwadeModConfigurationInterface,
    character: CharacterSheetInterface,
    sourceContainerUuid: string | null,
): ContainerTargetResult {
    // An offered option is never a target resolution: it is picked on a card and its pick is a record
    // whose configuration is the plain rung, so this branch is only ever reached by a stray flag.
    if (isOfferedOption(configuration)) return { targets: [], mode: 'unsupported' };
    const arrayName = supportedContainerPath(configuration.path);
    if (!arrayName) return { targets: [], mode: 'unsupported' };

    const containers = containersOnPath(character, configuration.path);

    const element = configuration.element;

    // "Pick one or more" is a prompt, exactly like `null` is: the add-time resolver and the
    // container-target menu turn it into a synthetic choice set before any applier sees it, so a
    // raw `[]` reaching this function means "not picked yet" and grants nothing.
    if (isEmptyList(element)) return { targets: [], mode: 'prompt' };

    // A container.* modification with NO element targets the container that granted it. Resolved
    // here to a concrete container so the caller records a uuid, never the word "self".
    if (element === undefined) {
        const own = containers.filter((container) => container?.uuid === sourceContainerUuid);
        return { targets: own, mode: 'self' };
    }

    // isPromptElement, not isElementUnset: `undefined` is handled by the `self` branch above, so the
    // two are equivalent HERE. Naming the shared predicate anyway is what keeps the prompt
    // resolver's `matches` — which has no such preceding branch — from drifting away from this line.
    if (isPromptElement(element)) return { targets: [], mode: 'prompt' };

    // All Specified (spec 2026-09-13 §3.1): every container of the listed elements, by selection
    // identity only — container identity stays reserved for records. Zero matches is still `all`:
    // the ledger entry is registered regardless and resolved again whenever the sheet changes.
    if (authoredTargetMode(configuration) === 'allSpecified') {
        return { targets: eligibleContainerTargets(character, configuration), mode: 'all' };
    }

    const wanted: string[] = (Array.isArray(element) ? element : [element])
        .map(elementEntryUuid)
        .filter((uuid): uuid is string => uuid !== null);
    let bySelection = false;
    const targets = containers.filter((container) => {
        // Container identity first: a player's pick names THIS container. Selection identity
        // second: an authored target (or a legacy record) names every container of that element.
        // Uuids do not collide, so a value matches at most one of the two.
        if (typeof container?.uuid === 'string' && wanted.includes(container.uuid)) return true;
        const uuid = selectionUuid(container);
        if (uuid !== null && wanted.includes(uuid)) { bySelection = true; return true; }
        return false;
    });

    return { targets, mode: bySelection ? 'bySelection' : 'byContainer' };
}

/**
 * Which containers one ledger entry hits — the single resolver behind recomputeContainerOverrides,
 * recalculatePower and the carried walker (spec 2026-09-13 §4.1). An All Specified entry ignores
 * its stored list (always `[]`) and resolves the eligible containers NOW; every other entry names
 * containers by the uuids it stored, a uuid no longer on the sheet or a blank one yielding nothing.
 */
export function effectTargets(character: CharacterSheetInterface, effect: ContainerEffect | null | undefined): any[] {
    const configuration = effect?.configuration;
    if (!configuration) return [];
    if (authoredTargetMode(configuration) === 'allSpecified') return eligibleContainerTargets(character, configuration);
    const stored = (Array.isArray(effect?.targetContainerUuids) ? effect!.targetContainerUuids : [])
        .filter((uuid): uuid is string => typeof uuid === 'string' && uuid.length > 0);
    if (stored.length === 0) return [];
    return containersOnPath(character, configuration.path)
        .filter((container) => typeof container?.uuid === 'string' && stored.includes(container.uuid));
}

// The sheet's records (additionalData.choices), or none when that is missing or malformed.
const choiceRecords = (character: CharacterSheetInterface | null | undefined): AppliedConfiguration[] => {
    const records: unknown = (character as { additionalData?: { choices?: unknown } } | null | undefined)?.additionalData?.choices;
    return Array.isArray(records) ? records as AppliedConfiguration[] : [];
};

// A record that picked this container: on a container path, with the container's uuid in its element.
// Only a pick writes such a record; a self or All Specified effect never does.
const recordNamesContainer = (record: AppliedConfiguration | null | undefined, containerUuid: string): boolean => {
    if (!record || !supportedContainerPath(record.configuration?.path)) return false;
    const element: unknown = record.configuration?.element;
    return (Array.isArray(element) ? element : [element]).some((entry) => elementEntryUuid(entry) === containerUuid);
};

/**
 * Another source has picked this container (spec 2026-10-13 §4): a record that is not the container's
 * own names it. A container's own records are its answers, not a targeting.
 */
export function isTargetedContainer(
    character: CharacterSheetInterface | null | undefined,
    container: { uuid?: unknown } | null | undefined,
): boolean {
    const uuid = container?.uuid;
    if (typeof uuid !== 'string' || uuid.length === 0) return false;
    return choiceRecords(character).some((record) => record?.uuid !== uuid && recordNamesContainer(record, uuid));
}

/**
 * Another copy of the same source already picked this container with the same modification (spec
 * 2026-10-13 §4a): a record from another source, keyed by this modification's uuid, names it. Copies
 * of one catalog element share their authored modification uuids, so a second Molecular Blade is not
 * offered the sword the first one took. A different item or an edge still is, and a source's own pick
 * stays offered to it — even where data saved before this rule has another copy naming it too — so
 * its pencil keeps working. A universal power modifier (world.modifications) is one modification
 * shared by every power, so a container one power picked with it is not offered to another (owner,
 * 2026-10-14).
 */
export function takenByAnotherCopy(
    character: CharacterSheetInterface | null | undefined,
    container: { uuid?: unknown } | null | undefined,
    pick: { sourceUuid: string, modUuid: string | undefined },
): boolean {
    const uuid = container?.uuid;
    if (typeof uuid !== 'string' || uuid.length === 0 || !pick.modUuid) return false;
    const naming = choiceRecords(character).filter((record) => recordNamesContainer(record, uuid)
        && resolvedModificationUuid(record) === pick.modUuid);
    return naming.length > 0 && naming.every((record) => record.uuid !== pick.sourceUuid);
}

/**
 * The ONE synthetic choice set for a container-target pick, used by the add-time resolver and by
 * the container-target menu. Options are the character's containers on the modification's path,
 * keyed by CONTAINER uuid and labelled by display name. Options come from `candidateContainerTargets`;
 * a container with no catalog element is never a candidate, and given the asking source's uuid, nor
 * is one another copy of that source already picked with this modification.
 */
export function buildContainerTargetChoiceSet(
    character: CharacterSheetInterface,
    mod: SwadeModificationInterface,
    sourceUuid?: string,
): SwadeModificationInterface | null {
    if (!supportedContainerPath(mod.configuration?.path)) return null;

    const pick = sourceUuid === undefined ? undefined : { sourceUuid, modUuid: mod.uuid };
    const containers = candidateContainerTargets(character, mod.configuration, pick);
    if (containers.length === 0) return null;

    return {
        uuid: mod.uuid,
        version: mod.version,
        multiple: pickerModeFor(mod.configuration.element) === 'multi',
        configuration: {
            path: 'character.choiceSet',
            element: containers.map((container) => ({ configuration: targetedConfiguration(mod.configuration, container) })),
            label: mod.configuration.label,
            summary: mod.configuration.summary,
            value: null,
            valueType: null,
            operator: null,
            mod_type: mod.configuration.mod_type,
        },
    } as SwadeModificationInterface;
}

/**
 * Transient picker state — NEVER persisted. The modification's records for this source, each
 * resolved to the containers it currently hits (so a legacy selection-uuid record pre-selects every
 * container of that element). With NO records, nothing is pre-selected: since spec r3
 * (whole-branch review ruling, 2026-09-14), a Specific Choices modification applies only through a
 * recorded pick, so a legacy fan-out entry — authored data with no record behind it — pre-selects
 * nothing here even while its effect is still live on the ledger. The first confirm through this
 * picker records the pick and, via reconcileRecordSets' rollback of the authored configuration by
 * key, retires that legacy entry.
 */
export function preselectionFor(
    character: CharacterSheetInterface,
    sourceUuid: string,
    mod: SwadeModificationInterface,
    records: AppliedConfiguration[] | undefined,
): AppliedConfiguration[] {
    const own = recordsForSourceModification(records, sourceUuid, mod.uuid);
    const configurations = own.map((r) => r.configuration);

    const seen = new Set<string>();
    const out: AppliedConfiguration[] = [];
    for (const configuration of configurations) {
        const { targets } = resolveContainerTargets(configuration, character, sourceUuid);
        for (const target of targets as any[]) {
            if (typeof target?.uuid !== 'string' || seen.has(target.uuid)) continue;
            seen.add(target.uuid);
            out.push({
                uuid: sourceUuid,
                sourceModUuid: mod.uuid,
                choiceSetUuid: mod.uuid,
                configuration: { ...configuration, element: target.uuid },
            });
        }
    }

    return out;
}

/**
 * Whether the pencil has anything to offer for one modification (spec 2026-09-10 §2.3, superseded
 * on this point by spec r3, 2026-09-14). Owns the identity guards too — a stable uuid (records
 * could not be told apart without one), a supported path, not self — so the composable and the menu
 * cannot drift.
 *
 * Every mode shows only when something is free to offer (spec 2026-10-13 §4a): never on an empty
 * path, never when every candidate is taken by another copy of the source. Pick-one and pick-many: any
 * free candidate. Specific mode compares the eligible pool with
 * the CURRENT target — the container a RECORD names, and nothing when there is no record, since a
 * Specific Choices modification applies only through a recorded pick — so a lone eligible container
 * is pickable until a record names it, two or more eligible containers are always a choice, and a
 * survivor the record no longer names is pickable rather than silently retargeted.
 */
export function containerTargetEditable(
    character: CharacterSheetInterface,
    sourceUuid: string,
    mod: SwadeModificationInterface,
    records: AppliedConfiguration[] | undefined,
): boolean {
    if (typeof mod?.uuid !== 'string' || mod.uuid.length === 0) return false;
    const configuration = mod.configuration;
    if (supportedContainerPath(configuration?.path) === undefined) return false;
    // All Specified is authored, never picked: nothing to retarget (spec 2026-09-13 §3.3).
    if (authoredTargetMode(configuration) === 'allSpecified') return false;
    const element = configuration?.element;
    if (!isRedirectableElement(element)) return false;

    // Only what this source may still pick, in every mode: never a container another copy of it already
    // picked with this modification, and nothing on an empty path — so the pencil never opens onto
    // nothing (spec 2026-10-13 §4a).
    const eligible = candidateContainerTargets(character, configuration, { sourceUuid, modUuid: mod.uuid });
    if (eligible.length === 0) return false;
    if (isPromptElement(element) || isEmptyList(element)) return true;
    if (eligible.length >= 2) return true;
    const targeted = preselectionFor(character, sourceUuid, mod, records).map((record) => record.configuration.element);
    return !targeted.includes(eligible[0].uuid);
}
